<template>
  <div class="mt-6">
    <div>
      <div class="relative bg-white rounded-2xl shadow-md p-6 mx-auto">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          <StackedField label="Encaissé par" :value="chargedBy" />
          <StackedField v-if="subscriptionReferenceId" label="Abonnement stripe">
            <span
              class="text-primary hover:text-primary-dark cursor-pointer break-all"
              @click="goToStripeSubscriptionDetails"
            >
              {{ subscriptionReferenceId }}
            </span>
          </StackedField>
        </div>
        <div class="mt-6">
          <Table :columns="columns" :data="rows">
            <template #amount="{ row }">
              <span
                :class="['text-sm font-medium', row.billingType === 'collected' ? 'text-green-500' : 'text-red-500']"
              >
                {{ row.amount }}
              </span>
            </template>
            <template #Store="{ row }">
              <span
                class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700"
              >
                {{ row.Store }}
              </span>
            </template>
            <template #Insurer="{ row }">
              <span
                class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600"
              >
                {{ row.Insurer }}
              </span>
            </template>
            <template #estalyCommission="{ row }">
              <span class="text-blue-900 bg-blue-100 px-2 py-1 rounded-xl text-xs">
                {{ row.estalyCommission }}
              </span>
            </template>
            <template #period="{ row }">
              <span class="text-sm font-medium text-gray-900">
                {{ row.period }}
              </span>
            </template>
            <template #paymentStatus="{ row }">
              <span class="text-sm font-medium text-gray-900">
                <div v-if="row.uncollectible === true">
                  {{ $t("planSaleBilling.paymentStatus.uncollectible") }}
                </div>
                <div v-else>
                  {{ $t("planSaleBilling.paymentStatus." + row.paymentStatus) }}
                </div>
              </span>
            </template>
            <template #invoice="{ row }">
              <div v-if="row.uncollectible" class="flex">
                <BanIcon class="h-6 w-6 text-gray-800" aria-hidden="true" />
              </div>
              <div v-else-if="row.discountApplied" class="flex">
                <GiftIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
              </div>
              <div v-else-if="row.paymentStatus === 'succeeded'" class="flex">
                <div v-if="row.invoiceUrl" @click="handleDownload(row)" class="cursor-pointer">
                  <DownloadIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
                </div>
              </div>
              <div v-else-if="['failed', 'disputed'].includes(row.paymentStatus)" class="flex">
                <div v-if="row.invoicePaymentUrl" @click="handlePayment(row)" class="cursor-pointer">
                  <CreditCardIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
                </div>
              </div>
              <div v-else class="px-3 py-0.5 rounded-full text-sm font-medium bg-secondary">
                <PauseIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
              </div>
            </template>
          </Table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DownloadIcon, GiftIcon, CreditCardIcon, BanIcon, PauseIcon } from "@heroicons/vue/outline";
import { Table, StackedField, formatPrice } from "@estaly/ui";
import { mapActions } from "vuex";

export default {
  props: ["planSaleBillings", "chargedBy", "subscriptionReferenceId"],
  components: {
    DownloadIcon,
    GiftIcon,
    CreditCardIcon,
    BanIcon,
    PauseIcon,
    Table,
    StackedField,
  },
  data: () => {
    return {
      openModal: false,
      commissionPlanSaleBilling: null,
    };
  },
  computed: {
    columns() {
      return [
        { label: "Montant", field: "amount", slot: "amount" },
        ...this.getSortedCommissions(this.planSaleBillings[0]?.commissions || []).map((commission) => ({
          label: commission.subjectType,
          field: commission.subjectType,
          slot: commission.subjectType,
        })),
        { label: "Estaly", field: "estalyCommission", slot: "estalyCommission" },
        { label: "Période", field: "period", slot: "period" },
        { label: "Statut", field: "paymentStatus", slot: "paymentStatus" },
        { label: "Facture", field: "invoice", slot: "invoice" },
      ];
    },
    rows() {
      return this.planSaleBillings.map((billing) => {
        const paymentStatus = billing.uncollectible ? "uncollectible" : billing.paymentStatus;
        return {
          ...billing,
          ...this.getSortedCommissions(billing.commissions).reduce((acc, commission) => {
            acc[commission.subjectType] = this.formatPrice(commission.amount);
            return acc;
          }, {}),
          estalyCommission: this.formatPrice(billing.estalyCommission),
          period: `${billing.startDate} au ${billing.endDate}`,
          amount: this.finalPrice(billing),
          paymentStatus: paymentStatus,
          rowClass: paymentStatus === "failed" ? "bg-red-100" : "",
        };
      });
    },
  },
  methods: {
    ...mapActions("planSales", ["getBillingInvoice"]),
    formatPrice,
    finalPrice(planSaleBilling) {
      return planSaleBilling.billingType === "collected"
        ? "+ " + this.formatPrice(planSaleBilling.finalPrice)
        : "- " + this.formatPrice(planSaleBilling.finalPrice);
    },
    handleDownload(planSaleBilling) {
      this.getBillingInvoice({ planSaleBillingId: planSaleBilling.id })
        .then((response) => {
          const url = response.data.invoiceUrl;
          if (url) window.open(url, "_blank");
        })
        .catch((error) => {
          console.error("Error fetching the invoice URL:", error);
        });
    },
    handlePayment(planSaleBilling) {
      this.getBillingInvoice({ planSaleBillingId: planSaleBilling.id })
        .then((response) => {
          const url = response.data.invoicePaymentUrl;
          if (url) window.open(url, "_blank");
        })
        .catch((error) => {
          console.error("Error fetching the payment URL:", error);
        });
    },
    getSortedCommissions(commissions) {
      // A negative priority means that the commission should be displayed first
      const subjectPriority = {
        Store: -1,
      };

      return commissions.slice().sort((a, b) => {
        let aPriority = subjectPriority[a.subjectType] || 0;
        let bPriority = subjectPriority[b.subjectType] || 0;

        return aPriority - bPriority;
      });
    },
    goToStripeSubscriptionDetails() {
      const url = `https://dashboard.stripe.com/subscriptions/${this.subscriptionReferenceId}`;
      window.open(url, "_blank");
    },
  },
};
</script>
